/* =========================================================
   ESSE WERKS — SHARE BAR
   Wires every [data-ew-share] instance on the page.

   - Web intents (X / Reddit / Facebook) open a sized popup with noopener.
   - Copy Link writes the UTM-tagged URL to the clipboard (with a
     textarea+execCommand fallback for insecure context / older browsers)
     and flashes "Link copied" in the bar's absolutely-positioned status
     span (no layout shift).
   - Native uses navigator.share when present; the native button is hidden
     in markup and revealed here only when navigator.share exists.
   - Each successful interaction fires window.ewTrack('share', {...}).
     ewTrack (ew-global.js) owns the GA4/consent contract; we only call it,
     wrapped in try/catch so it can never throw.

   Loaded with `defer` from the host section, so the DOM is parsed by the
   time this runs; the DOMContentLoaded guard below is belt-and-braces.
   ========================================================= */
(function () {
  'use strict';

  function withUtm(base, source, campaign, content) {
    try {
      var u = new URL(base);
      u.searchParams.set('utm_source', source);
      u.searchParams.set('utm_medium', 'social');
      if (campaign) u.searchParams.set('utm_campaign', campaign);
      if (content) u.searchParams.set('utm_content', content);
      return u.toString();
    } catch (e) {
      return base;
    }
  }

  function openIntent(url) {
    /* Sized popup, noopener — never rely on the returned handle. */
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=620');
  }

  function track(method, campaign, itemId, placement) {
    try {
      window.ewTrack && window.ewTrack('share', {
        method: method,
        content_type: campaign,
        item_id: itemId,
        placement: placement
      });
    } catch (e) { /* never throw on telemetry */ }
  }

  /* Clipboard write with a fallback for insecure context / older browsers. */
  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      try {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        var ok = document.execCommand('copy');
        document.body.removeChild(ta);
        ok ? resolve() : reject(new Error('execCommand copy failed'));
      } catch (err) {
        reject(err);
      }
    });
  }

  function wireBar(bar) {
    if (bar.__ewShareWired) return;
    bar.__ewShareWired = true;

    var campaign = bar.getAttribute('data-share-campaign') || 'tech_essay';
    var itemId = bar.getAttribute('data-share-id') || '';
    var placement = bar.getAttribute('data-share-placement') || '';
    var statusEl = bar.querySelector('.ew-share-bar__status');
    var statusTimer = null;

    function flashStatus(msg) {
      if (!statusEl) return;
      statusEl.textContent = msg;
      if (statusTimer) clearTimeout(statusTimer);
      statusTimer = setTimeout(function () {
        statusEl.textContent = '';
      }, 2000);
    }

    var buttons = bar.querySelectorAll('[data-share-method]');
    Array.prototype.forEach.call(buttons, function (btn) {
      var method = btn.getAttribute('data-share-method');

      /* Reveal the native button only on touch-primary devices that also have
         the Web Share API. Feature-detect alone isn't enough: macOS Chrome and
         Safari support navigator.share, so a pure API check would flash the
         native button on desktop. `(pointer: coarse)` gates it to phones/tablets
         where the OS share sheet (Instagram / WhatsApp / Messages) is the
         intended affordance; desktop stays hidden. */
      if (method === 'native') {
        var isTouchPrimary = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
        if (typeof navigator.share === 'function' && isTouchPrimary) {
          btn.removeAttribute('hidden');
        }
      }

      btn.addEventListener('click', function () {
        /* Read URL/title LIVE each click: a host may set data-share-url after
           wire time (e.g. the Tune Builder computes it client-side at
           DOMContentLoaded via window.ewConfigurator.toShareUrl()). Static
           hosts set it server-side, so reading at click is behaviorally
           identical for them. */
        var base = bar.getAttribute('data-share-url') || '';
        var title = bar.getAttribute('data-share-title') || '';

        if (method === 'x') {
          var xUrl = 'https://twitter.com/intent/tweet?url=' +
            encodeURIComponent(withUtm(base, 'x', campaign, itemId)) +
            '&text=' + encodeURIComponent(title);
          openIntent(xUrl);
          track('x', campaign, itemId, placement);

        } else if (method === 'reddit') {
          var rUrl = 'https://www.reddit.com/submit?url=' +
            encodeURIComponent(withUtm(base, 'reddit', campaign, itemId)) +
            '&title=' + encodeURIComponent(title);
          openIntent(rUrl);
          track('reddit', campaign, itemId, placement);

        } else if (method === 'facebook') {
          var fUrl = 'https://www.facebook.com/sharer/sharer.php?u=' +
            encodeURIComponent(withUtm(base, 'facebook', campaign, itemId));
          openIntent(fUrl);
          track('facebook', campaign, itemId, placement);

        } else if (method === 'copy_link') {
          copyText(withUtm(base, 'copy_link', campaign, itemId)).then(function () {
            flashStatus('Link copied');
            track('copy_link', campaign, itemId, placement);
          }).catch(function () {
            flashStatus('Copy unavailable');
          });

        } else if (method === 'native') {
          if (typeof navigator.share !== 'function') return;
          navigator.share({
            title: title,
            url: withUtm(base, 'native', campaign, itemId)
          }).then(function () {
            track('native', campaign, itemId, placement);
          }).catch(function (err) {
            /* User dismissed the sheet — not a share. Do not fire. */
            if (err && err.name === 'AbortError') return;
          });
        }
      });
    });
  }

  function init() {
    var bars = document.querySelectorAll('[data-ew-share]');
    Array.prototype.forEach.call(bars, wireBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
